import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { axiosInstance } from "../../../utils/axios";
import dayjs from "dayjs";
import AdminSidebar from "../../../components/AdminSidebar";
import LoadingComponent from "../../../components/LoadingComponent";
import NoData from "../../../components/NoData";
import Button from "../../../components/Button";
import { formatCoveragePeriod } from "../../../utils/paymentCoverage";
import {
  ArrowLeftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/20/solid";

const QUARTER_STYLES = {
  Q1: {
    header: "from-emerald-500 to-emerald-600",
    badge: "bg-emerald-100 text-emerald-700 border-emerald-200",
    monthBg: "bg-emerald-50 text-emerald-700 border-emerald-200",
    dot: "bg-emerald-500",
    label: "Q1 · Jan – Mar",
  },
  Q2: {
    header: "from-sky-500 to-sky-600",
    badge: "bg-sky-100 text-sky-700 border-sky-200",
    monthBg: "bg-sky-50 text-sky-700 border-sky-200",
    dot: "bg-sky-500",
    label: "Q2 · Apr – Jun",
  },
  Q3: {
    header: "from-amber-500 to-amber-600",
    badge: "bg-amber-100 text-amber-700 border-amber-200",
    monthBg: "bg-amber-50 text-amber-700 border-amber-200",
    dot: "bg-amber-500",
    label: "Q3 · Jul – Sep",
  },
  Q4: {
    header: "from-violet-500 to-violet-600",
    badge: "bg-violet-100 text-violet-700 border-violet-200",
    monthBg: "bg-violet-50 text-violet-700 border-violet-200",
    dot: "bg-violet-500",
    label: "Q4 · Oct – Dec",
  },
};

const DEFAULT_STYLE = {
  header: "from-gray-400 to-gray-500",
  badge: "bg-gray-100 text-gray-700 border-gray-200",
  monthBg: "bg-gray-50 text-gray-700 border-gray-200",
  dot: "bg-gray-400",
  label: "—",
};

export default function RentalPaymentHistory() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const perPage = 6;

  // Fetch rental details
  const { data: rental, isLoading: rentalLoading } = useQuery({
    queryKey: ["device-rental", id],
    queryFn: async () => {
      const response = await axiosInstance.get(
        `${window.REACT_APP_API_ENDPOINT}/device-rentals/${id}`
      );
      return response.data.data || response.data;
    },
  });

  // Fetch payments
  const { data: paymentsData, isLoading: paymentsLoading, isFetching } = useQuery({
    queryKey: ["rental-payments", id, page],
    queryFn: async () => {
      const response = await axiosInstance.get(
        `${window.REACT_APP_API_ENDPOINT}/device-rentals-payments/by-rental/${id}?page=${page}&per_page=${perPage}`
      );
      return response.data;
    },
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  const payments = paymentsData?.data?.data || [];
  const currentPage = paymentsData?.data?.current_page || 1;
  const lastPage = paymentsData?.data?.last_page || 1;
  const total = paymentsData?.data?.total || 0;
  const from = paymentsData?.data?.from || 0;
  const to = paymentsData?.data?.to || 0;

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(lastPage, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  };

  if (rentalLoading) {
    return (
      <AdminSidebar>
        <div className="flex items-center justify-center min-h-screen bg-bgBlue">
          <LoadingComponent />
        </div>
      </AdminSidebar>
    );
  }

  return (
    <AdminSidebar>
      <div className="px-4 sm:px-6 lg:px-8 bg-bgBlue min-h-screen pb-12">
        <div className="max-w-7xl mx-auto">
          {/* ── Header ── */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center py-6 gap-4">
            <div className="flex items-center gap-4">
              <Button
                variant="secondary"
                size="sm"
                icon={<ArrowLeftIcon className="h-4 w-4" />}
                onClick={() => navigate(-1)}
              >
                Back
              </Button>
              <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">
                Payment History
              </h1>
            </div>
            <div className="inline-flex items-center gap-2 bg-white border border-gray-200 rounded-full px-4 py-2 shadow-sm">
              <span className="w-2 h-2 rounded-full bg-bluePrimary animate-pulse" />
              <span className="text-sm font-semibold text-gray-700">
                {total} payment{total !== 1 ? "s" : ""} recorded
              </span>
            </div>
          </div>

          {/* ── Rental Summary Card ── */}
          <div className="bg-white shadow-sm rounded-xl overflow-hidden border border-gray-100 mb-8">
            <div className="bg-gradient-to-r from-slate-50 to-blue-50 px-6 py-3 border-b border-gray-100">
              <h2 className="text-sm font-bold text-gray-700 uppercase">
                Rental Information
              </h2>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <SummaryField label="Client" value={rental?.client_name || rental?.client?.name} />
              {/* <SummaryField label="Brand" value={rental?.brand} /> */}
              <SummaryField label="Serial Number" value={rental?.serial_number} />
              <SummaryField label="Status" value={
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${rental?.status === "Active"
                  ? "bg-green-100 text-green-700"
                  : rental?.status === "Returned"
                    ? "bg-gray-100 text-gray-700"
                    : "bg-blue-100 text-blue-700"
                  }`}>
                  {rental?.status || "—"}
                </span>
              } />
              <SummaryField
                label="Monthly Rental"
                value={rental?.monthly_rental_price ? `CHF ${rental.monthly_rental_price}` : null}
              />
              <SummaryField
                label="Contract Start"
                value={rental?.contract_start_date ? dayjs(rental.contract_start_date).format("DD/MM/YYYY") : null}
              />
              <SummaryField
                label="Expected End"
                value={rental?.expected_end_date ? dayjs(rental.expected_end_date).format("DD/MM/YYYY") : null}
              />
              <SummaryField label="Location" value={rental?.location} />
              <SummaryField label="Email" value={rental?.email} />
            </div>
          </div>

          {/* ── Payment Cards Grid ── */}
          {paymentsLoading ? (
            <div className="flex items-center justify-center py-20">
              <LoadingComponent />
            </div>
          ) : payments.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm py-16">
              <NoData message="No payment records found for this rental." />
            </div>
          ) : (
            <>
              <div
                className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 transition-opacity duration-200 ${isFetching ? "opacity-50" : "opacity-100"
                  }`}
              >
                {payments.map((payment) => {
                  const style = QUARTER_STYLES[payment.quarter] || DEFAULT_STYLE;
                  const paidMonths = Array.isArray(payment.paid_months)
                    ? payment.paid_months
                    : typeof payment.paid_months === "string"
                      ? JSON.parse(payment.paid_months)
                      : [];

                  return (
                    <div
                      key={payment.id}
                      className="bg-white rounded-2xl shadow-sm hover:shadow-xl border border-gray-100 overflow-hidden transition-all duration-300 ease-in-out group"
                    >
                      {/* ── Colored Top Header ── */}
                      <div className={`bg-gradient-to-r ${style.header} px-5 py-4 flex items-center justify-between`}>
                        <div className="flex items-center gap-3">
                          <span className="text-white text-3xl font-extrabold leading-none tracking-tight">
                            {payment.year}
                          </span>
                          <div className="flex flex-col">
                            <span className="text-white/90 text-sm font-bold leading-tight">
                              {payment.quarter}
                            </span>
                            <span className="text-white/60 text-xs leading-tight">
                              {style.label.split(" · ")[1]}
                            </span>
                          </div>
                        </div>
                        <span className="bg-white/20 backdrop-blur-sm text-white text-xs font-bold px-2.5 py-1 rounded-full">
                          #{payment.id}
                        </span>
                      </div>

                      {/* ── Card Body ── */}
                      <div className="p-5 flex flex-col gap-5">
                        {/* Payment Date Row */}
                        <div className="flex items-start gap-3">
                          <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-4.5 w-4.5 text-gray-500"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={1.5}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                              />
                            </svg>
                          </div>
                          <div className="flex flex-col min-w-0">
                            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                              Payment Date
                            </span>
                            <span className="text-base font-bold text-gray-900 mt-0.5">
                              {payment.payment_date
                                ? dayjs(payment.payment_date).format("DD MMMM YYYY")
                                : "—"}
                            </span>
                          </div>
                        </div>

                        {/* Coverage Period Row (from → to) */}
                        <div className="flex items-start gap-3">
                          <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-4.5 w-4.5 text-gray-500"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={1.5}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                              />
                            </svg>
                          </div>
                          <div className="flex flex-col min-w-0">
                            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                              Covered Period
                            </span>
                            <span className="text-base font-bold text-gray-900 mt-0.5">
                              {formatCoveragePeriod(paidMonths, payment.year)}
                            </span>
                          </div>
                        </div>

                        {/* Months Count Row */}
                        <div className="flex items-start gap-3">
                          <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-4.5 w-4.5 text-gray-500"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={1.5}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                          </div>
                          <div className="flex flex-col min-w-0">
                            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                              Months Covered
                            </span>
                            <span className="text-base font-bold text-gray-900 mt-0.5">
                              {paidMonths.length} month{paidMonths.length !== 1 ? "s" : ""}
                            </span>
                          </div>
                        </div>

                        {/* Total Amount Row */}
                        <div className="flex items-start gap-3">
                          <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-4.5 w-4.5 text-gray-500"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={1.5}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                          </div>
                          <div className="flex flex-col min-w-0">
                            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                              Total Amount
                            </span>
                            <span className="text-base font-bold text-gray-900 mt-0.5">
                              {payment.total_amount != null
                                ? `CHF ${parseFloat(payment.total_amount).toFixed(2)}`
                                : "—"}
                            </span>
                          </div>
                        </div>

                        {/* Paid Months Tags */}
                        {paidMonths.length > 0 && (
                          <div className="pt-1">
                            <div className="flex flex-wrap gap-2">
                              {paidMonths.map((month, idx) => (
                                <span
                                  key={idx}
                                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border ${style.monthBg}`}
                                >
                                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                    <path
                                      fillRule="evenodd"
                                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                  {month}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* ── Pagination ── */}
              {lastPage > 1 && (
                <div className="flex flex-col sm:flex-row items-center justify-between mt-8 bg-white rounded-xl border border-gray-100 shadow-sm px-6 py-4 gap-4">
                  <span className="text-sm text-gray-500">
                    Showing{" "}
                    <span className="font-semibold text-gray-700">{from}</span>–
                    <span className="font-semibold text-gray-700">{to}</span> of{" "}
                    <span className="font-semibold text-gray-700">{total}</span>{" "}
                    payments
                  </span>

                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage <= 1}
                      className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeftIcon className="h-5 w-5" />
                    </button>

                    {getPageNumbers().map((pageNum) => (
                      <button
                        key={pageNum}
                        onClick={() => setPage(pageNum)}
                        className={`min-w-[40px] h-10 rounded-lg text-sm font-medium transition-all duration-150 ${pageNum === currentPage
                          ? "bg-bluePrimary text-white shadow-md shadow-blue-200"
                          : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                          }`}
                      >
                        {pageNum}
                      </button>
                    ))}

                    <button
                      onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
                      disabled={currentPage >= lastPage}
                      className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronRightIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </AdminSidebar>
  );
}

function SummaryField({ label, value }) {
  return (
    <div className="flex flex-col">
      <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
        {label}
      </label>
      <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 font-medium">
        {value || "—"}
      </div>
    </div>
  );
}
